import React, { useState, useEffect, useCallback, useRef } from 'react'
import { API_BASE_URL } from '../config/api';
import {
  Download, Search, Filter, Calendar, User, FileText, Trash2,
  Eye, Send, X, CheckCircle, Clock, AlertCircle, ChevronDown, ChevronUp,
  RefreshCw
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import HelpButton from '../components/HelpButton'
import BotonVisualizar from '../components/BotonVisualizar'
import { usePageTour } from '../hooks/usePageTour'
import {
  clasificarEstadoDocumento,
  claseBadgeEstado,
  formatearNombreRechazo,
  normalizarEstadoFirmante,
} from '../utils/documentoEstadoUi'

// ─── Modal de Trazabilidad ────────────────────────────────────────────────────
const TrazabilidadModal = ({ documentoId, onClose }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const cargar = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const res = await fetch(`${API_BASE_URL}/api/documentos/${documentoId}/trazabilidad`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Error al cargar trazabilidad')
      setData(await res.json())
    } catch (e) {
      toast.error(e.message)
      onClose()
    } finally {
      setLoading(false)
    }
  }, [documentoId, onClose])

  useEffect(() => { cargar() }, [cargar])

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' }) : null

  const estadoBadge = (ev) => {
    const norm = normalizarEstadoFirmante(ev)
    if (norm === 'firmado')   return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800"><CheckCircle className="h-3 w-3" />Firmado</span>
    if (norm === 'en_espera') return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600"><Clock className="h-3 w-3" />En espera</span>
    if (norm === 'rechazado') return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800"><AlertCircle className="h-3 w-3" />Rechazado</span>
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800"><Clock className="h-3 w-3" />Pendiente</span>
  }

  const docEstadoColor = (documentoOEstado) => {
    const meta = typeof documentoOEstado === 'string'
      ? clasificarEstadoDocumento({ estado: documentoOEstado })
      : clasificarEstadoDocumento(documentoOEstado || {})
    return claseBadgeEstado(meta.estadoUi)
  }

  const docEstadoLabel = (documentoOEstado) => {
    const meta = typeof documentoOEstado === 'string'
      ? clasificarEstadoDocumento({ estado: documentoOEstado })
      : clasificarEstadoDocumento(documentoOEstado || {})
    if (meta.estadoUi === 'completado') return 'Completado'
    if (meta.estadoUi === 'en_proceso') return 'En proceso de firma'
    if (meta.estadoUi === 'pendiente') return 'Pendiente de inicio'
    if (meta.estadoUi === 'rechazado' || meta.estadoUi === 'cancelado_por_rechazo') return 'Rechazado'
    if (meta.estadoUi === 'cancelado_por_vencimiento') return 'Cancelado por vencimiento'
    if (meta.estadoUi === 'cancelado_otra_causa') return 'Cancelado'
    return meta.etiqueta
  }

  const handleDescargar = async () => {
    if (!data) return
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const res = await fetch(`${API_BASE_URL}/api/documentos/${documentoId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || 'Error al descargar')
      }
      const cd = res.headers.get('Content-Disposition') || ''
      const utf8 = cd.match(/filename\*=UTF-8''(.+?)(?:;|$)/)
      const plain = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      const filename = utf8 ? decodeURIComponent(utf8[1]) : (plain ? plain[1].replace(/['"]/g, '') : `documento_${documentoId}.pdf`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = filename; a.click()
      setTimeout(() => URL.revokeObjectURL(url), 200)
      toast.success('Documento descargado')
    } catch (e) {
      toast.error('Error al descargar: ' + e.message)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Eye className="h-5 w-5 text-blue-600" />
            Trazabilidad del documento
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Cargando trazabilidad...</p>
            </div>
          </div>
        ) : data && (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 text-base mb-1">{data.titulo}</h3>
              {data.descripcion && <p className="text-sm text-gray-500 mb-2">{data.descripcion}</p>}
              <div className="flex flex-wrap gap-2 items-center">
                <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${docEstadoColor(data)}`}>
                  {docEstadoLabel(data)}
                </span>
                {data.requiere_orden_firma && (
                  <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">Firma secuencial</span>
                )}
              </div>
              <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                <div>Propietario: <span className="font-medium text-gray-700">{data.owner_nombre || data.owner_email}</span></div>
                <div>Creado: <span className="font-medium text-gray-700">{fmt(data.fecha_creacion)}</span></div>
                {data.fecha_completado && <div>Completado: <span className="font-medium text-green-700">{fmt(data.fecha_completado)}</span></div>}
              </div>
              {clasificarEstadoDocumento(data).canceladoPorRechazo && data.rechazo && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 space-y-1">
                  <div className="font-semibold">Detalle del rechazo</div>
                  {formatearNombreRechazo(data.rechazo) && (
                    <div>Firmante: <span className="font-medium">{formatearNombreRechazo(data.rechazo)}</span></div>
                  )}
                  {data.rechazo.fecha_rechazo && (
                    <div>Fecha: <span className="font-medium">{fmt(data.rechazo.fecha_rechazo)}</span></div>
                  )}
                  <div>Motivo: <span className="font-medium">{data.rechazo.motivo || 'Sin motivo especificado'}</span></div>
                </div>
              )}
              {clasificarEstadoDocumento(data).estadoUi === 'cancelado_otra_causa' && (
                <div className="mt-3 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
                  Documento cancelado por otra causa (sin rechazo de firmante registrado).
                </div>
              )}
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-sm font-medium text-gray-700">Progreso de firmas</span>
                <span className="text-sm font-bold text-gray-900">{data.firmantes_completados} de {data.total_firmantes} ({data.porcentaje_completado}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${data.porcentaje_completado === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                  style={{ width: `${data.porcentaje_completado}%` }}
                />
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Línea de tiempo</h4>
              <div className="space-y-0">
                {data.firmantes.map((f, idx) => {
                  const esUltimo = idx === data.firmantes.length - 1
                  const est = normalizarEstadoFirmante(f.estado_visual || f.estado)
                  return (
                    <div key={f.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold border-2 ${
                          est === 'firmado'   ? 'bg-green-500 border-green-500 text-white' :
                          est === 'en_espera' ? 'bg-gray-100 border-gray-300 text-gray-400' :
                          est === 'rechazado' ? 'bg-red-100 border-red-400 text-red-600' :
                                                'bg-amber-100 border-amber-400 text-amber-700'
                        }`}>
                          {est === 'firmado' ? '✓' : f.orden_firma}
                        </div>
                        {!esUltimo && <div className={`w-0.5 flex-1 my-1 min-h-[20px] ${est === 'firmado' ? 'bg-green-300' : 'bg-gray-200'}`} />}
                      </div>
                      <div className="flex-1 pb-4">
                        <div className="flex flex-wrap items-center gap-2 mb-0.5">
                          <span className="text-sm font-medium text-gray-900">{f.nombre} {f.apellido}</span>
                          {estadoBadge(est)}
                        </div>
                        <div className="text-xs text-gray-400">{f.email}</div>
                        {est === 'rechazado' && f.fecha_firma && (
                          <div className="text-xs text-red-600 mt-0.5 font-medium">Rechazado el {fmt(f.fecha_firma)}</div>
                        )}
                        {est === 'rechazado' && (
                          <div className="text-xs text-red-700 mt-0.5">Motivo: {f.motivo_rechazo || 'Sin motivo especificado'}</div>
                        )}
                        {est === 'firmado' && f.fecha_firma && (
                          <div className="text-xs text-green-600 mt-0.5 font-medium">Firmado el {fmt(f.fecha_firma)}</div>
                        )}
                        {!f.fecha_firma && f.fecha_invitacion && est !== 'en_espera' && est !== 'rechazado' && (
                          <div className="text-xs text-gray-400 mt-0.5">Invitado el {fmt(f.fecha_invitacion)}</div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Cerrar
          </button>
          {data?.estado === 'completado' && (
            <button onClick={handleDescargar} className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium">
              <Download className="h-4 w-4" />
              Descargar PDF
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const PAGE_SIZE = 25

const vistaDePestana = (tab) => (tab === 'en_seguimiento' ? 'seguimiento' : 'mis_documentos')

// ─── Componente principal ─────────────────────────────────────────────────────
const SignaturesHistory = () => {
  const { user } = useAuth()
  const [signatures, setSignatures] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('mis_documentos')
  const [trazabilidadId, setTrazabilidadId] = useState(null)
  // IDs de tarjetas con la sección de firmantes expandida
  const [expandidos, setExpandidos] = useState({})
  // Estado del botón refresh de seguimiento
  const [refreshing, setRefreshing] = useState(false)
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null)

  const isSupervisor = user?.role === 'supervisor'
  const isAdmin = user?.email === 'admin@induma.com.co'

  const [filters, setFilters] = useState({ usuario: '', fecha_inicio: '', fecha_fin: '' })
  const [appliedFilters, setAppliedFilters] = useState({ usuario: '', fecha_inicio: '', fecha_fin: '' })
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [tabTotals, setTabTotals] = useState({ mis_documentos: 0, en_seguimiento: 0 })
  const [claveCargada, setClaveCargada] = useState('')
  const solicitud = useRef(0)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [documentoToDelete, setDocumentoToDelete] = useState(null)
  const [showReenvioModal, setShowReenvioModal] = useState(false)
  const [documentoToReenviar, setDocumentoToReenviar] = useState(null)
  const [reenviando, setReenviando] = useState(false)

  usePageTour('signed-tracking-page', { enabled: !loading })

  const buildQueryString = (f, pageNumber, tab) => {
    const p = new URLSearchParams()
    p.set('page', String(pageNumber))
    p.set('page_size', String(PAGE_SIZE))
    p.set('vista', vistaDePestana(tab))
    if (f.usuario?.trim()) p.append('usuario', f.usuario.trim())
    if (f.fecha_inicio) p.append('fecha_inicio', f.fecha_inicio)
    if (f.fecha_fin) p.append('fecha_fin', f.fecha_fin)
    return `?${p.toString()}`
  }

  const claveConsulta = `${activeTab}|${page}|${appliedFilters.usuario}|${appliedFilters.fecha_inicio}|${appliedFilters.fecha_fin}`

  const loadSignatures = useCallback(async ({ mantenerLista = false } = {}) => {
    const idSolicitud = ++solicitud.current
    const clave = `${activeTab}|${page}|${appliedFilters.usuario}|${appliedFilters.fecha_inicio}|${appliedFilters.fecha_fin}`
    try {
      if (mantenerLista) setRefreshing(true)
      else {
        setLoading(true)
        setSignatures([])
      }
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      if (!token) { toast.error('No hay token de autenticación'); return }
      const q = buildQueryString(appliedFilters, page, activeTab)
      const res = await fetch(`${API_BASE_URL}/api/documentos/historial${q}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Error al cargar historial')
      const data = await res.json()
      if (idSolicitud !== solicitud.current) return false
      const items = Array.isArray(data) ? data : (data.items || [])
      const nextTotal = Array.isArray(data) ? items.length : (data.total ?? items.length)
      const nextPages = Array.isArray(data) ? 1 : (data.total_pages ?? 0)
      setSignatures(items)
      setTotal(nextTotal)
      setTotalPages(nextPages)
      setTabTotals(prev => ({ ...prev, [activeTab]: nextTotal }))
      setClaveCargada(clave)
      setUltimaActualizacion(new Date())
      return true
    } catch (e) {
      if (idSolicitud !== solicitud.current) return false
      setSignatures([])
      setTotal(0)
      setTotalPages(0)
      setClaveCargada(clave)
      toast.error('Error al cargar el historial: ' + e.message)
      return false
    } finally {
      if (idSolicitud === solicitud.current) {
        setLoading(false)
        setRefreshing(false)
      }
    }
  }, [activeTab, appliedFilters, page])

  useEffect(() => { loadSignatures() }, [loadSignatures])

  useEffect(() => {
    if (loading) return
    window.dispatchEvent(new Event('scroll'))
  }, [loading, total])

  const buscar = () => {
    setAppliedFilters({ ...filters })
    setPage(1)
  }

  const limpiarFiltros = () => {
    const vacios = { usuario: '', fecha_inicio: '', fecha_fin: '' }
    setFilters(vacios)
    setAppliedFilters(vacios)
    setPage(1)
  }

  const cambiarPestana = (tab) => {
    setActiveTab(tab)
    setPage(1)
  }

  const handleRefreshSeguimiento = async () => {
    if (refreshing) return
    const ok = await loadSignatures({ mantenerLista: true })
    if (ok) toast.success('Documentos actualizados')
  }

  const toggleExpandido = (id) => {
    setExpandidos(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const abrirModalEliminar = (documentoId, estado) => {
    const e = (estado || '').toLowerCase()
    if (e === 'completado') { toast.error('No se pueden eliminar documentos completados'); return }
    if (!['pendiente', 'en_proceso', 'cancelado'].includes(e)) { toast.error('Solo se pueden eliminar documentos pendientes, en proceso o rechazados'); return }
    setDocumentoToDelete({ id: documentoId, estado })
    setShowDeleteModal(true)
  }

  const eliminarDocumento = async () => {
    if (!documentoToDelete) return
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const res = await fetch(`${API_BASE_URL}/api/documentos/${documentoToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Error al eliminar') }
      toast.success('Documento eliminado exitosamente')
      setShowDeleteModal(false)
      setDocumentoToDelete(null)
      loadSignatures()
    } catch (e) {
      toast.error('Error al eliminar documento: ' + e.message)
      setShowDeleteModal(false)
      setDocumentoToDelete(null)
    }
  }

  const abrirModalReenvio = (documento) => {
    setDocumentoToReenviar(documento)
    setShowReenvioModal(true)
  }

  const firmantesRechazadosDe = (documento) =>
    (documento?.firmantes || []).filter((f) => (f.estado || '').toLowerCase() === 'rechazado')

  const reenviarDocumento = async () => {
    if (!documentoToReenviar || reenviando) return
    setReenviando(true)
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const res = await fetch(`${API_BASE_URL}/api/documentos/${documentoToReenviar.id}/reenviar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      })
      if (!res.ok) {
        const e = await res.json().catch(() => ({}))
        const detalle = e.detail || 'Error al reenviar'
        throw new Error(typeof detalle === 'string' ? detalle : JSON.stringify(detalle))
      }
      const d = await res.json()
      toast.success(d.message || 'Documento reenviado exitosamente')
      setShowReenvioModal(false)
      setDocumentoToReenviar(null)
      loadSignatures()
    } catch (e) {
      toast.error('Error al reenviar documento: ' + e.message)
    } finally {
      setReenviando(false)
    }
  }

  const handleDownload = (documentoId) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (!token) { toast.error('No hay token de autenticación'); return }
    fetch(`${API_BASE_URL}/api/documentos/${documentoId}/download`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(async res => {
      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem('token'); localStorage.removeItem('user')
          sessionStorage.removeItem('token'); sessionStorage.removeItem('user')
          toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.')
          setTimeout(() => { window.location.href = '/login' }, 1500)
          return
        }
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || 'Error al descargar archivo')
      }
      const cd = res.headers.get('Content-Disposition') || ''
      const utf8 = cd.match(/filename\*=UTF-8''(.+?)(?:;|$)/)
      const plain = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      const filename = utf8 ? decodeURIComponent(utf8[1]) : (plain ? plain[1].replace(/['"]/g, '') : `documento_${documentoId}.pdf`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = filename
      a.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0'
      document.body.appendChild(a)
      setTimeout(() => { a.click(); setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a) }, 200) }, 50)
      toast.success('Documento descargado exitosamente')
    })
    .catch(e => { console.error(e); toast.error('Error al descargar: ' + e.message) })
  }

  const formatDate = (d) => d ? new Date(d).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) : '—'
  const formatHora = (d) => d ? new Date(d).toLocaleString('es-CO', { timeStyle: 'short' }) : null
  const formatEstadoDocumento = (documento) => clasificarEstadoDocumento(documento).etiqueta

  const documentosPagina = signatures
  const datosListos = claveCargada === claveConsulta && !loading
  const conteoPestana = (tab, valor) => (activeTab === tab && !datosListos ? 0 : (valor || 0))
  const totalMisDocumentos = conteoPestana('mis_documentos', tabTotals.mis_documentos)
  const totalSeguimiento = conteoPestana('en_seguimiento', tabTotals.en_seguimiento)

  // ── Sección colapsable de firmantes ──────────────────────────────────────────
  const renderFirmantesLista = (documento) => {
    if (!documento.firmantes?.length) return null

    const totalFirm   = documento.total_firmantes ?? documento.firmantes.length
    const firmados    = documento.firmantes_completados ?? documento.firmantes.filter(f => f.estado === 'firmado').length
    const pct         = totalFirm > 0 ? Math.round((firmados / totalFirm) * 100) : 0
    const expandido   = !!expandidos[documento.id]
    const tieneOrden  = !!documento.requiere_orden_firma

    return (
      <div className="mt-3 pt-3 border-t border-gray-100">
        {/* Fila resumen + toggle */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {tieneOrden && (
              <span className="text-xs font-medium text-gray-500">Orden de firmas activado</span>
            )}
            <span className="text-sm font-semibold text-primary-600">
              {firmados} de {totalFirm} {tieneOrden ? 'completados' : 'firmantes'}
              {totalFirm > 0 ? ` (${pct}%)` : ''}
            </span>
          </div>
          <button
            onClick={() => toggleExpandido(documento.id)}
            aria-expanded={expandido}
            className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 transition-colors px-2 py-1 rounded hover:bg-primary-50"
          >
            {expandido ? (
              <><ChevronUp className="h-3.5 w-3.5" /><span>Ocultar firmantes</span></>
            ) : (
              <><ChevronDown className="h-3.5 w-3.5" /><span>Ver firmantes</span></>
            )}
          </button>
        </div>

        {/* Barra de progreso — siempre visible */}
        {tieneOrden && totalFirm > 0 && (
          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
            <div
              className={`h-1.5 rounded-full transition-all duration-500 ${pct === 100 ? 'bg-green-500' : 'bg-primary-600'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}

        {/* Lista de firmantes — colapsable con animación */}
        <div
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{ maxHeight: expandido ? `${documento.firmantes.length * 56}px` : '0px', opacity: expandido ? 1 : 0 }}
        >
          <ul className="space-y-2 mt-3">
            {documento.firmantes
              .slice()
              .sort((a, b) => (a.orden_firma ?? 999) - (b.orden_firma ?? 999))
              .map(f => (
                <li key={f.id} className="flex flex-wrap items-center gap-2 text-sm">
                  {tieneOrden && (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-800 font-semibold text-xs flex-shrink-0">
                      {f.orden_firma ?? '-'}
                    </span>
                  )}
                  <span className="text-gray-700 truncate">{f.nombre} {f.apellido} – {f.email}</span>
                  {(() => {
                    const est = normalizarEstadoFirmante(f.estado_visual || f.estado)
                    return (
                      <>
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          est === 'firmado'   ? 'bg-green-100 text-green-800' :
                          est === 'rechazado' ? 'bg-red-100 text-red-800' :
                          est === 'en_espera' ? 'bg-gray-100 text-gray-600' :
                                               'bg-amber-100 text-amber-800'
                        }`}>
                          {est === 'firmado'   && '✓ Firmado'}
                          {est === 'pendiente' && '⏳ Pendiente'}
                          {est === 'en_espera' && '⏸️ En espera'}
                          {est === 'rechazado' && '✕ Rechazado'}
                          {!['firmado','pendiente','en_espera','rechazado'].includes(est) && (est || 'Pendiente')}
                        </span>
                        {f.fecha_firma && (
                          <span className="text-gray-500 text-xs">{formatDate(f.fecha_firma)}</span>
                        )}
                        {est === 'rechazado' && (
                          <span className="text-red-700 text-xs w-full basis-full">Motivo: {f.motivo_rechazo || 'Sin motivo especificado'}</span>
                        )}
                      </>
                    )
                  })()}
                </li>
              ))}
          </ul>
        </div>
      </div>
    )
  }

  // ── Tarjeta de documento normal ───────────────────────────────────────────────
  const renderDocumentoCard = (documento) => {
    const currentUser = user || (() => { try { return JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || 'null') } catch { return null } })()
    const esPropietario = documento && currentUser && Number(documento.owner_id) === Number(currentUser?.id)
    const estadoLower = (documento.estado || '').toLowerCase()
    const estadoMeta = clasificarEstadoDocumento(documento)
    const todosRechazados = documento.firmantes?.every(f => (f.estado || '').toLowerCase() === 'rechazado')
    const tieneFirmantesRechazados = documento.firmantes?.some(f => (f.estado || '').toLowerCase() === 'rechazado')
    const esAdmin = currentUser?.email === 'admin@induma.com.co'
    const mostrarBotonEliminar = (esAdmin || esPropietario) && (['pendiente','en_proceso','cancelado'].includes(estadoLower) || todosRechazados) && estadoLower !== 'completado'
    const contextoReenvio = esPropietario && (
      estadoLower === 'cancelado' ||
      (estadoLower === 'pendiente' && tieneFirmantesRechazados) ||
      (estadoLower === 'en_proceso' && tieneFirmantesRechazados)
    )
    const reenvioVencido = estadoMeta.reenvioVencido === true || documento.reenvio_vencido === true
    const mostrarBotonReenviar = contextoReenvio
    const reenviarHabilitado = mostrarBotonReenviar && !reenvioVencido && documento.puede_reenviar !== false && estadoMeta.puedeReenviar !== false
    const completado = estadoLower === 'completado'

    return (
      <div key={documento.id} className="border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow duration-200">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{documento.titulo}</h3>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {documento.tipo && documento.tipo !== 'todos' && (
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${documento.tipo === 'enviado' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                    {documento.tipo === 'enviado' ? 'Enviado' : 'Recibido'}
                  </span>
                )}
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${claseBadgeEstado(estadoMeta.estadoUi)}`}>
                  {formatEstadoDocumento(documento)}
                </span>
              </div>
            </div>

            {documento.descripcion && <p className="text-gray-600 mb-3">{documento.descripcion}</p>}

            {estadoMeta.canceladoPorRechazo && estadoMeta.rechazo && (
              <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <span className="font-medium">Rechazo:</span>{' '}
                {formatearNombreRechazo(estadoMeta.rechazo) || 'Firmante'}
                {estadoMeta.rechazo.fecha_rechazo ? ` · ${formatDate(estadoMeta.rechazo.fecha_rechazo)}` : ''}
                {' · '}Motivo: {estadoMeta.rechazo.motivo || 'Sin motivo especificado'}
              </div>
            )}
            {reenvioVencido && (
              <div className="mb-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900">
                {estadoMeta.mensajePlazo || documento.mensaje_plazo_reenvio || 'El plazo de 5 días para reenviar este documento ha vencido'}
              </div>
            )}
            {estadoMeta.estadoUi === 'cancelado_otra_causa' && (
              <div className="mb-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800">
                Cancelado por otra causa (sin rechazo de firmante).
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm text-gray-500 mb-1">
              <div className="flex items-center gap-2"><Calendar className="h-4 w-4 flex-shrink-0" /><span>Creado: {formatDate(documento.fecha_creacion)}</span></div>
              {documento.owner_nombre && (
                <div className="flex items-center gap-2"><User className="h-4 w-4 flex-shrink-0" /><span>Por: <strong className="text-gray-700">{documento.owner_nombre}</strong></span></div>
              )}
              {documento.visualizadores?.length > 0 && (
                <div className="flex items-center gap-2"><Eye className="h-4 w-4 text-blue-500 flex-shrink-0" /><span>Visualizadores: {documento.visualizadores.map(v => `${v.nombre} ${v.apellido}`).join(', ')}</span></div>
              )}
            </div>

            {renderFirmantesLista(documento)}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 lg:ml-4 lg:flex-shrink-0 w-full sm:w-auto">
            {mostrarBotonReenviar && (
              <button
                onClick={() => { if (reenviarHabilitado) abrirModalReenvio(documento) }}
                disabled={!reenviarHabilitado || reenviando}
                title={reenvioVencido ? (estadoMeta.mensajePlazo || 'El plazo de 5 días para reenviar este documento ha vencido') : 'Reenviar a firmantes rechazados'}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap text-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
              >
                <Send className="h-4 w-4 flex-shrink-0" /><span>Reenviar</span>
              </button>
            )}
            {mostrarBotonEliminar && (
              <button onClick={() => abrirModalEliminar(documento.id, documento.estado)}
                className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap text-sm">
                <Trash2 className="h-4 w-4 flex-shrink-0" /><span>Eliminar</span>
              </button>
            )}
            <BotonVisualizar documentoId={documento.id} />
            <button
              disabled={!completado || isSupervisor}
              onClick={() => { if (completado && !isSupervisor) handleDownload(documento.id) }}
              title={isSupervisor ? 'Sin permisos' : !completado ? 'Solo disponible cuando esté completado' : 'Descargar documento'}
              className={`w-full sm:w-auto px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap text-sm ${
                !completado || isSupervisor ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'
              }`}>
              <Download className="h-4 w-4 flex-shrink-0" /><span>Descargar</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Tarjeta de documento en seguimiento ──────────────────────────────────────
  const renderSeguimientoCard = (documento) => {
    const estadoMeta  = clasificarEstadoDocumento(documento)
    const completado  = documento.estado === 'completado'
    const totalFirm   = documento.total_firmantes ?? documento.firmantes?.length ?? 0
    const firmados    = documento.firmantes_completados ?? documento.firmantes?.filter(f => f.estado === 'firmado').length ?? 0
    const pct         = totalFirm > 0 ? Math.round((firmados / totalFirm) * 100) : 0
    const expandido   = !!expandidos[`seg_${documento.id}`]

    return (
      <div key={documento.id} className="border border-blue-100 bg-blue-50/40 rounded-xl p-4 sm:p-5 hover:shadow-md transition-shadow duration-200">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Eye className="h-4 w-4 text-blue-500 flex-shrink-0" />
              <h3 className="text-base font-semibold text-gray-900">{documento.titulo}</h3>
              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${claseBadgeEstado(estadoMeta.estadoUi)}`}>
                {formatEstadoDocumento(documento)}
              </span>
            </div>

            {documento.descripcion && <p className="text-sm text-gray-500 mb-2">{documento.descripcion}</p>}

            {estadoMeta.canceladoPorRechazo && estadoMeta.rechazo && (
              <div className="mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                <span className="font-medium">Rechazo:</span>{' '}
                {formatearNombreRechazo(estadoMeta.rechazo) || 'Firmante'}
                {estadoMeta.rechazo.fecha_rechazo ? ` · ${formatDate(estadoMeta.rechazo.fecha_rechazo)}` : ''}
                {' · '}Motivo: {estadoMeta.rechazo.motivo || 'Sin motivo especificado'}
              </div>
            )}
            {estadoMeta.estadoUi === 'cancelado_otra_causa' && (
              <div className="mb-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
                Cancelado por otra causa (sin rechazo de firmante).
              </div>
            )}

            <div className="text-xs text-gray-500 mb-3 flex flex-wrap gap-x-4 gap-y-1">
              <span>Creado: <strong className="text-gray-700">{formatDate(documento.fecha_creacion)}</strong></span>
              {documento.owner_nombre && <span>Por: <strong className="text-gray-700">{documento.owner_nombre}</strong></span>}
            </div>

            {/* Barra de progreso — siempre visible */}
            <div className="mb-2">
              <div className="flex justify-between items-center mb-1 text-xs">
                <span className="font-medium text-gray-600">Progreso de firmas</span>
                <span className="font-bold text-gray-800">{firmados} de {totalFirm} ({pct}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div className={`h-2 rounded-full transition-all duration-500 ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                  style={{ width: `${pct}%` }} />
              </div>
            </div>

            {/* Toggle firmantes */}
            {documento.firmantes?.length > 0 && (
              <>
                <button
                  onClick={() => toggleExpandido(`seg_${documento.id}`)}
                  aria-expanded={expandido}
                  className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors px-2 py-1 rounded hover:bg-blue-100 mt-1"
                >
                  {expandido ? (
                    <><ChevronUp className="h-3.5 w-3.5" /><span>Ocultar firmantes</span></>
                  ) : (
                    <><ChevronDown className="h-3.5 w-3.5" /><span>Ver firmantes</span></>
                  )}
                </button>

                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{ maxHeight: expandido ? `${documento.firmantes.length * 40}px` : '0px', opacity: expandido ? 1 : 0 }}
                >
                  <ul className="space-y-1.5 mt-2">
                    {documento.firmantes.slice().sort((a, b) => (a.orden_firma ?? 999) - (b.orden_firma ?? 999)).map(f => {
                      const est = normalizarEstadoFirmante(f.estado_visual || f.estado)
                      return (
                      <li key={f.id} className="flex items-center gap-2 text-sm">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          est === 'firmado'   ? 'bg-green-500' :
                          est === 'rechazado' ? 'bg-red-500' :
                          est === 'en_espera' ? 'bg-gray-300' : 'bg-amber-400'
                        }`} />
                        <span className="text-gray-700 truncate">{f.nombre} {f.apellido}</span>
                        <span className={`ml-auto flex-shrink-0 text-xs font-medium ${
                          est === 'firmado'   ? 'text-green-600' :
                          est === 'rechazado' ? 'text-red-600' :
                          est === 'en_espera' ? 'text-gray-400' : 'text-amber-600'
                        }`}>
                          {est === 'firmado'   && `✓ ${f.fecha_firma ? formatDate(f.fecha_firma) : 'Firmado'}`}
                          {est === 'rechazado' && `✕ ${f.fecha_firma ? formatDate(f.fecha_firma) : 'Rechazado'}`}
                          {est === 'en_espera' && '⏸ En espera'}
                          {est === 'pendiente' && '⏳ Pendiente'}
                        </span>
                      </li>
                      )
                    })}
                  </ul>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col gap-2 lg:ml-4 lg:flex-shrink-0 w-full lg:w-36">
            <BotonVisualizar documentoId={documento.id} className="self-end" />
            <button
              onClick={() => setTrazabilidadId(documento.id)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
            >
              <span>Ver detalle</span>
            </button>
            {completado && !isSupervisor ? (
              <button onClick={() => handleDownload(documento.id)}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm">
                <Download className="h-4 w-4" /><span>Descargar</span>
              </button>
            ) : !completado && (
              <div className="w-full px-3 py-2 bg-gray-100 text-gray-400 rounded-lg flex items-center justify-center gap-2 text-xs text-center">
                <Download className="h-3.5 w-3.5 flex-shrink-0" /><span>Al completar</span>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderPaginacion = () => {
    if (total <= 0) return null
    const desde = ((page - 1) * PAGE_SIZE) + 1
    const hasta = Math.min(page * PAGE_SIZE, total)
    return (
      <div id="tour-news-signed-pagination" className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6">
        <span className="text-sm text-gray-500">
          {desde}-{hasta} de {total} · Página {page} de {Math.max(totalPages, 1)}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage(actual => Math.max(1, actual - 1))}
            disabled={page <= 1 || loading}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Anterior
          </button>
          <button
            type="button"
            onClick={() => setPage(actual => actual + 1)}
            disabled={page >= totalPages || loading}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Siguiente
          </button>
        </div>
      </div>
    )
  }

  // ── Render principal ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: 'var(--background-color)' }}>
      <header className="main-header">
        <div className="header-content">
          <div className="header-left">
            <div id="tour-signed-header" className="header-title">
              <h1>Firmados y Pendientes</h1>
              <p>{isAdmin ? 'Vista de administrador: todos los documentos del sistema' : 'Gestiona y descarga documentos enviados y recibidos'}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Filtros */}
        <div id="tour-signed-filters" className="card mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">Filtros</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
              <input type="text" value={filters.usuario} onChange={e => setFilters(p => ({ ...p, usuario: e.target.value }))} placeholder="Buscar por usuario..." className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
              <input type="date" value={filters.fecha_inicio} onChange={e => setFilters(p => ({ ...p, fecha_inicio: e.target.value }))} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
              <input type="date" value={filters.fecha_fin} onChange={e => setFilters(p => ({ ...p, fecha_fin: e.target.value }))} className="input-field" />
            </div>
            <div className="flex items-end gap-2">
              <button onClick={buscar} className="btn-primary flex-1 flex items-center justify-center gap-2">
                <Search className="h-4 w-4" /><span>Buscar</span>
              </button>
              <button onClick={limpiarFiltros} className="btn-secondary">Limpiar</button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div id="tour-signed-tabs" className="flex border-b border-gray-200 mb-6">
          <button
            id="tour-signed-tab-mis-documentos"
            type="button"
            onClick={() => cambiarPestana('mis_documentos')}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'mis_documentos' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Mis documentos
            {totalMisDocumentos > 0 && (
              <span className={`ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full ${activeTab === 'mis_documentos' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'}`}>
                {totalMisDocumentos}
              </span>
            )}
          </button>
          <button
            id="tour-signed-tab-seguimiento"
            type="button"
            onClick={() => cambiarPestana('en_seguimiento')}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'en_seguimiento' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            En seguimiento
            {totalSeguimiento > 0 && (
              <span className={`ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full ${activeTab === 'en_seguimiento' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                {totalSeguimiento}
              </span>
            )}
          </button>
        </div>

        {/* Contenido */}
        <div id="tour-news-signed-list">
        {(loading || !datosListos) ? (
          <div className="card text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4" />
            <p className="text-gray-600">Cargando...</p>
          </div>
        ) : activeTab === 'mis_documentos' ? (
          <div id="tour-signed-document-list" className="card">
            {/* Header "Mis documentos" con botón refresh y timestamp */}
            <div className="flex items-center justify-between mb-4">
              {isAdmin ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex-1 mr-3">
                  <p className="text-sm text-blue-800"><strong>Vista de Administrador:</strong> {total} documento(s). Esta página muestra {documentosPagina.length}.</p>
                </div>
              ) : (
                <span className="text-sm text-gray-500">{total} documento(s)</span>
              )}
              <div className="flex items-center gap-3 flex-shrink-0">
                {ultimaActualizacion && (
                  <span className="text-xs text-gray-400">
                    Actualizado: {ultimaActualizacion.toLocaleTimeString('es-CO', { timeStyle: 'short' })}
                  </span>
                )}
                <button
                  id="tour-signed-refresh"
                  onClick={() => loadSignatures()}
                  disabled={loading}
                  aria-label="Actualizar mis documentos"
                  aria-busy={loading}
                  title="Actualizar documentos"
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                    loading
                      ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
                      : 'border-blue-200 text-blue-600 bg-white hover:bg-blue-50 hover:border-blue-400'
                  }`}
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>{loading ? 'Actualizando...' : 'Actualizar'}</span>
                </button>
              </div>
            </div>
            {documentosPagina.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay documentos registrados</h3>
                <p className="text-gray-600">{isAdmin ? 'No hay documentos en el sistema' : 'Los documentos aparecerán aquí una vez que se envíen o reciban'}</p>
              </div>
            ) : (
              <div className="space-y-4">{documentosPagina.map(d => renderDocumentoCard(d))}</div>
            )}
            {renderPaginacion()}
          </div>
        ) : (
          <div id="tour-signed-document-list">
            {/* Header de seguimiento con botón refresh */}
            <div className="flex items-center justify-end mb-4 gap-3">
              {ultimaActualizacion && (
                <span className="text-xs text-gray-400">
                  Actualizado: {ultimaActualizacion.toLocaleTimeString('es-CO', { timeStyle: 'short' })}
                </span>
              )}
              <button
                id="tour-signed-refresh"
                onClick={handleRefreshSeguimiento}
                disabled={refreshing}
                aria-label="Actualizar documentos en seguimiento"
                aria-busy={refreshing}
                title="Actualizar datos"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                  refreshing
                    ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'border-blue-200 text-blue-600 bg-white hover:bg-blue-50 hover:border-blue-400'
                }`}
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Actualizando...' : 'Actualizar'}</span>
              </button>
            </div>

            {documentosPagina.length === 0 ? (
              <div className="card text-center py-12">
                <Eye className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Sin documentos en seguimiento</h3>
                <p className="text-gray-500">Aquí aparecerán los documentos donde eres visualizador, con su progreso en tiempo real.</p>
              </div>
            ) : (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex items-start gap-3">
                  <Eye className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    Estás siguiendo <strong>{total}</strong> documento(s). Esta página muestra {documentosPagina.length}. Puedes ver el progreso en tiempo real.
                    Una vez completados, podrás descargarlos.
                  </p>
                </div>
                <div className="space-y-4">{documentosPagina.map(d => renderSeguimientoCard(d))}</div>
              </>
            )}
            {renderPaginacion()}
          </div>
        )}
        </div>
      </div>

      {/* Modal Trazabilidad */}
      {trazabilidadId && (
        <TrazabilidadModal documentoId={trazabilidadId} onClose={() => setTrazabilidadId(null)} />
      )}

      {/* Modal Eliminar */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">¿Eliminar documento?</h3>
              <p className="text-sm text-gray-600 text-center mb-6">Esta acción no se puede deshacer.</p>
              <div className="flex gap-3">
                <button onClick={() => { setShowDeleteModal(false); setDocumentoToDelete(null) }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">Cancelar</button>
                <button onClick={eliminarDocumento}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium">Eliminar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reenvío tras rechazo */}
      {showReenvioModal && documentoToReenviar && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => { if (!reenviando) { setShowReenvioModal(false); setDocumentoToReenviar(null) } }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-blue-100 rounded-full mb-4">
                <Send className="h-6 w-6 text-blue-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                ¿Reenviar a firmantes rechazados?
              </h3>
              <p className="text-sm text-gray-600 text-center mb-4">
                Se generará un nuevo enlace para cada firmante rechazado. El rechazo original y su motivo
                se conservan en el historial. Los demás firmantes no se modifican.
              </p>
              {(() => {
                const rechazados = firmantesRechazadosDe(documentoToReenviar)
                const rechazoUi = clasificarEstadoDocumento(documentoToReenviar).rechazo
                if (rechazados.length > 0) {
                  return (
                    <ul className="mb-6 max-h-48 overflow-y-auto space-y-2 text-sm border border-red-100 rounded-md bg-red-50 p-3">
                      {rechazados.map((f) => (
                        <li key={f.id} className="text-red-900">
                          <span className="font-medium">{f.nombre} {f.apellido}</span>
                          <span className="text-red-700"> · {f.email}</span>
                          <div className="text-red-800">
                            Motivo: {f.motivo_rechazo || 'Sin motivo especificado'}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )
                }
                if (rechazoUi) {
                  return (
                    <div className="mb-6 text-sm border border-red-100 rounded-md bg-red-50 p-3 text-red-900">
                      <div className="font-medium">{formatearNombreRechazo(rechazoUi) || 'Firmante rechazado'}</div>
                      <div>Motivo: {rechazoUi.motivo || 'Sin motivo especificado'}</div>
                    </div>
                  )
                }
                return (
                  <p className="mb-6 text-sm text-center text-gray-500">No hay firmantes rechazados listados.</p>
                )
              })()}
              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={reenviando}
                  onClick={() => { setShowReenvioModal(false); setDocumentoToReenviar(null) }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-60"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={reenviando}
                  onClick={reenviarDocumento}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-60"
                >
                  {reenviando ? 'Reenviando...' : 'Sí, reenviar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <HelpButton tourId="signed-tracking-page" id="tour-signed-help" />
    </div>
  )
}

export default SignaturesHistory
